import { PropsWithChildren, useEffect, useRef, useState } from 'react'

type Props = {
  className?: string
  disableScroll?: boolean
}

const ScrollableDiv = ({
  children,
  className,
  disableScroll = false
}: PropsWithChildren<Props>) => {
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [isScrollable, setIsScrollable] = useState(false) // State to track if the div is scrollable
  const boxRef = useRef<HTMLDivElement | null>(null)

  // Check if the div is scrollable
  useEffect(() => {
    const box = boxRef.current
    if (box) {
      setIsScrollable(box.scrollWidth > box.clientWidth) // Check if content is overflowing horizontally
    }
  }, [children]) // Rerun this when children change

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isScrollable && disableScroll) return // Do nothing if not scrollable
    if (e.target instanceof Node && !e.currentTarget.contains(e.target)) return // Prevent dragging when clicking on a non-child element

    setIsDragging(true)
    if (!boxRef.current) return
    setStartX(e.pageX - boxRef.current.offsetLeft)
    setScrollLeft(boxRef.current.scrollLeft)
  }

  const handleMouseLeaveOrUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isScrollable || disableScroll) return
    e.preventDefault()
    if (!boxRef.current) return
    const x = e.pageX - boxRef.current.offsetLeft
    const walk = (x - startX) * 2 // Adjust the scroll sensitivity
    boxRef.current.scrollLeft = scrollLeft - walk
  }

  return (
    <div
      className={className}
      ref={boxRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeaveOrUp}
      onMouseUp={handleMouseLeaveOrUp}
      onMouseMove={handleMouseMove}
      style={{
        cursor: isDragging ? 'grabbing' : isScrollable ? 'grab' : 'default',
        overflowX: 'auto',
        userSelect: isDragging ? 'none' : 'auto'
      }}
    >
      {children}
    </div>
  )
}

export default ScrollableDiv
